<script lang="ts">
    import type {Document, Exemplar, ViewableField} from 'src/typings';
    import {getInjector, getLocalization} from 'src/core';
    import {pipe} from 'core/utils';
    import {KpDocumentMiniPresenter} from './kp-document-mini.presenter';
    import {characters, inline, loc, prop} from 'shared/utils/pipes';
    import {getFieldsRecursively, getSubfields, WELL_KNOWN_FIELDS} from 'shared/utils/record-utils';
    import {exists} from 'shared/utils/custom-utils';
    import KpCover from '../kp-cover/KpCover.svelte';
    import Labels from 'shared/components/kp-label/Labels.svelte';

    export let document: Document;
    export let exemplar: Exemplar = null;

    const localize = getLocalization();
    const presenter = getInjector().getByClass(KpDocumentMiniPresenter);

    let seriesText: string;
    let sf245c: ViewableField[];
    let sfAltAuthors: ViewableField[];
    $: {
        seriesText = presenter.getSeriesText(document);
        sf245c = presenter.getSf245c(document);
        sfAltAuthors = presenter.getSfAltAuthors(sf245c, document);
    }
</script>

<div class="document-mini-container">
    {#if !document.external}
        <div class="cover-container cover-container-document-mini">
            <KpCover record={document} fillTo="width"/>
        </div>
    {/if}
    <div class="document-mini-content">
        {#if document.external}
            <span>{document.name}</span>
        {:else}
            <a href="/records/{document.id}" data-qa="document-name-{document.id}">{document.name}</a>
        {/if}

        {#if document.subtitle && document.subtitle.length > 0}
            <div class="supplementaryText">
                <Labels labeleds={document.subtitle} linkifyUrls={true}/>
            </div>
        {/if}
        {#if exists(document.fields)}
            {#if seriesText}
                <div class="supplementaryText">{seriesText}</div><!-- serie -->
            {/if}

            {#if exists(sf245c)}
                <div class="supplementaryText">{pipe(sf245c, prop('text'), inline())}</div>
            {/if}

            {#if exists(sfAltAuthors)}
                <div class="supplementaryText">{pipe(sfAltAuthors, prop('text'), inline())}</div>
            {/if}

            <div class="supplementaryText">{pipe(getSubfields(document, WELL_KNOWN_FIELDS.documentPublicationOld.code, [WELL_KNOWN_FIELDS.documentPublicationOld.place.code, WELL_KNOWN_FIELDS.documentPublicationOld.publisher.code, WELL_KNOWN_FIELDS.documentPublicationOld.year.code]), prop('text'), inline(' '))}</div>
            <div class="supplementaryText">{pipe(getSubfields(document, WELL_KNOWN_FIELDS.documentPublicationRda.code, [WELL_KNOWN_FIELDS.documentPublicationRda.place.code, WELL_KNOWN_FIELDS.documentPublicationRda.publisher.code, WELL_KNOWN_FIELDS.documentPublicationRda.year.code]), prop('text'), inline(' '))}</div>

            {#if !exemplar?.signature}
                <div class="supplementaryText">{pipe(getSubfields(document, WELL_KNOWN_FIELDS.locationStatements.code, [WELL_KNOWN_FIELDS.locationStatements.signature.code]), prop('text'), inline(), characters(30))}</div>
            {/if}

            <div class="supplementaryText">{pipe(getFieldsRecursively(document, [
                {
                    [WELL_KNOWN_FIELDS.sourceDocument.code]: [
                        {[WELL_KNOWN_FIELDS.sourceDocument.refholder.code]: [WELL_KNOWN_FIELDS.sourceDocument.refholder.t.code, WELL_KNOWN_FIELDS.sourceDocument.refholder.d.code, WELL_KNOWN_FIELDS.sourceDocument.refholder.h.code, WELL_KNOWN_FIELDS.sourceDocument.refholder.x.code, WELL_KNOWN_FIELDS.sourceDocument.refholder.s.code]},
                        WELL_KNOWN_FIELDS.sourceDocument.g.code, WELL_KNOWN_FIELDS.sourceDocument.f.code, WELL_KNOWN_FIELDS.sourceDocument.v.code
                    ]
                }
            ]), prop('text'), inline(' '))}</div>
        {/if}

        {#if exists(exemplar)}
            <!-- div to encapsulate inline <span> elements in block element -->
            <div>
                {#if exists(exemplar.signature)}
                    <span>{localize(/* @kp-localization exemplar.signature */ 'exemplar.signature')}: {exemplar.signature}</span>
                {/if}

                {#if exists(exemplar.barCode)}
                    <span>{localize(/* @kp-localization exemplar.barCode */ 'exemplar.barCode')}: {exemplar.barCode}</span>
                {/if}

                {#if (!exemplar.signature && !exemplar.barCode && exists(exemplar.accessNumber))}
                    <!-- access number zobrazime jen pokud exemplar nema signaturu ani BC -->
                    <span>{localize(/* @kp-localization exemplar.accessNumber */ 'exemplar.accessNumber')}: {exemplar.accessNumber}</span>
                {/if}

                <!-- ISSUES -->
                {#if presenter.hasVolume(exemplar)}
                    {#await presenter.fetchVolumeForExemplar(exemplar) then volume}
                        <span>
                            {localize(/* @kp-localization commons.rocnik */ 'commons.rocnik')}: {pipe(volume, loc())}
                            {#if exists(volume.year)}
                                <span>({volume.year})</span>
                            {/if}
                        </span>
                    {/await}
                {/if}

                {#if exists(exemplar.issueName)}
                    <span>{localize(/* @kp-localization exemplar.issueName */ 'exemplar.issueName')}: {exemplar.issueName}</span>
                {/if}

                <!-- BUNDLED VOLUMES -->
                {#if exists(exemplar.bundledVolumeNumber)}
                    <span>{localize(/* @kp-localization exemplar.bundledVolumeNumber */ 'exemplar.bundledVolumeNumber')}: {exemplar.bundledVolumeNumber}</span>
                {/if}

                {#if exists(exemplar.bundledVolumeYear)}
                    <span>{localize(/* @kp-localization exemplar.bundledVolumeYear */ 'exemplar.bundledVolumeYear')}: {exemplar.bundledVolumeYear}</span>
                {/if}

                {#if exists(exemplar.bundledVolumeIssueRange)}
                    <span>{localize(/* @kp-localization exemplar.bundledVolumeIssueRange */ 'exemplar.bundledVolumeIssueRange')}: {exemplar.bundledVolumeIssueRange}</span>
                {/if}
            </div>
        {/if}
    </div>
</div>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .document-mini-container {
        display: flex;
        flex-direction: row;
    }

    .cover-container-document-mini {
        flex-shrink: 0;
        width: 50px;
        transition: 0.2s ease-in-out opacity;

        &:hover {
            opacity: 0.7;
        }
    }

    .document-mini-content {
        margin-left: @spacing-m;
    }

    .supplementaryText {
        color: gray;
        font-size: 0.9em;
    }
</style>
